import { useState } from 'react';
import { NJButton, NJCheckbox, NJFormItem, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import { getMapboxToken, UMSelect, UMSegmentedControl } from '../../urbanmap/_components';
import { CUSTOMERS, energyLabel } from './data';
import { sameHypotheses } from './hooks';
import { phasingWarnings } from './phasing';
import { NameField } from './NameField';
import type { DevelopmentPlan, Hypotheses } from './types';

export function PlanPanel({ plan, hypotheses, update, calculate, draw, busy, error }: {
  plan: DevelopmentPlan; hypotheses: Hypotheses; update: (patch: Partial<DevelopmentPlan>) => void;
  calculate: () => void; draw: () => void; busy: boolean; error?: string;
}) {
  const [tab, setTab] = useState('Abonnés');
  const count = Object.keys(plan.subscribers).length;
  const missingSubscribers = Object.values(plan.subscribers).filter(d => !d).length;
  const missingPipes = plan.pipes.filter(p => !p.month).length;
  const hasTrace = !!plan.snapshot || plan.pipes.length > 0;
  const status = plan.stale ? 'À recalculer' : !hasTrace ? 'Brouillon' :
    missingPipes || missingSubscribers ? 'Phasage à compléter' : 'Complet';
  const warnings = phasingWarnings(plan, plan.snapshot ?? hypotheses);
  return <section className="sp-panel sp-stack">
    <div className="sp-row sp-between">
      <NJHeading as="h2" scale="md">Plan de développement</NJHeading>
      <NJText as="div" className="sp-badge">{busy ? 'Calcul en cours…' : status}</NJText>
    </div>
    <NameField label="Nom du plan" value={plan.name} save={name => update({ name })} />
    <NJText as="div" scale="sm">Type du réseau et des abonnés</NJText>
    <UMSelect ariaLabel="Type du plan" value={plan.energy} options={[
      { value: 'heat', label: 'Chaleur' }, { value: 'cold', label: 'Froid' },
    ]} onChange={value => {
      if (value === 'heat' || value === 'cold') update({ energy: value, stale: hasTrace || plan.stale });
    }} />
    {plan.snapshot && !sameHypotheses(plan.snapshot, hypotheses) && <div className="sp-notice">
      <NJText scale="sm">Le scénario a été modifié depuis le calcul. Au prochain recalcul, vous choisirez les hypothèses à utiliser.</NJText>
    </div>}
    <div className="sp-summary">
      <NJText as="div"><strong>{count}</strong> abonnés · {energyLabel(plan.energy)}</NJText>
      <NJText as="div"><strong>{plan.pipes.length}</strong> canalisations</NJText>
    </div>
    <UMSegmentedControl options={['Abonnés', 'Canalisations']} value={tab} onChange={setTab} />
    {tab === 'Abonnés' ? <>
      <NJText scale="sm">Sélectionnez les abonnés puis renseignez leur mois de raccordement. Phasage obligatoire avant calcul.</NJText>
      <ul className="sp-list">{CUSTOMERS.map(customer => {
        const checked = customer.id in plan.subscribers;
        return <li key={customer.id} className="sp-card">
          <NJCheckbox label={customer.name} isChecked={checked} onChange={(_, selected) => {
            const subscribers = { ...plan.subscribers };
            if (selected) subscribers[customer.id] = '';
            else delete subscribers[customer.id];
            update({ subscribers, stale: hasTrace || plan.stale });
          }} />
          <NJText as="div" scale="sm">{energyLabel(plan.energy)} · {customer.demand.toLocaleString('fr-FR')} MWh/an · estimation de démonstration</NJText>
          {checked && <NJFormItem id={`month-${customer.id}`} label={`Raccordement · ${customer.name}`} type="month"
            value={plan.subscribers[customer.id]} hasError={!plan.subscribers[customer.id]}
            subscriptMessage={!plan.subscribers[customer.id] ? 'Mois et année requis' : undefined}
            onChange={e => { if (e.target instanceof HTMLInputElement) update({ subscribers: { ...plan.subscribers, [customer.id]: e.target.value } }); }} />}
        </li>;
      })}</ul>
    </> : <>
      <NJButton label="Dessiner une canalisation" scale="sm" icon="draw" emphasis="subtle"
        disabled={!getMapboxToken()} onClick={draw} />
      {!getMapboxToken() && <NJText scale="sm">Dessin indisponible sans token Mapbox valide.</NJText>}
      <NJText scale="sm">Chaque canalisation doit être phasée. Les dessins manuels ne passent pas par l’algorithme et seront supprimés après un recalcul réussi, sur confirmation.</NJText>
      {!plan.pipes.length && <NJText>Aucune canalisation. Calculez le tracé ou dessinez-en une sur la carte.</NJText>}
      <ul className="sp-list">{plan.pipes.map((pipe, i) => <li className="sp-card" key={pipe.id}>
        <NJText as="div"><strong>{i + 1}. {pipe.name}</strong></NJText>
        <NJText as="div" scale="sm">{pipe.manual ? 'Dessin manuel' : 'Tracé calculé'}</NJText>
        <NJFormItem id={`pipe-${pipe.id}`} label={`Réalisation · ${pipe.name}`} type="month" value={pipe.month}
          hasError={!pipe.month} subscriptMessage={!pipe.month ? 'Phasage requis pour compléter le plan' : undefined}
          onChange={e => {
            if (!(e.target instanceof HTMLInputElement)) return;
            const month = e.target.value;
            update({ pipes: plan.pipes.map(p => p.id === pipe.id ? { ...p, month } : p) });
          }} />
        {pipe.manual && <NJButton label={`Supprimer ${pipe.name}`} scale="sm" emphasis="minimal"
          onClick={() => update({ pipes: plan.pipes.filter(p => p.id !== pipe.id) })} />}
      </li>)}</ul>
      {missingPipes > 0 && <NJText as="div" role="status">{missingPipes} canalisation(s) à phaser pour compléter le plan.</NJText>}
    </>}
    {warnings.length > 0 && <div className="sp-warning" role="status">
      <NJText as="div"><strong>Incohérences de phasage · non bloquantes</strong></NJText>
      {warnings.map(w => <NJText as="div" scale="sm" key={w}>{w}</NJText>)}
    </div>}
    {error && <div className="sp-error" role="alert"><NJText>{error}</NJText></div>}
    <div className="sp-action-bar">
      {missingSubscribers > 0 && <NJText as="div" scale="sm">{missingSubscribers} abonné(s) à phaser avant calcul.</NJText>}
      {!count && <NJText as="div" scale="sm">Sélectionnez au moins un abonné.</NJText>}
      <NJButton label={plan.snapshot ? 'Recalculer le tracé' : 'Générer le tracé'} icon="route"
        isLoading={busy} disabled={busy || !count || missingSubscribers > 0 || !plan.name.trim()} onClick={calculate} />
      <NJText as="div" scale="sm">Simulation locale · aucun coût financier calculé</NJText>
    </div>
  </section>;
}
